'use client';

import React, { useState, useMemo } from 'react';
import { Dna, RefreshCw, BarChart2, CheckCircle2 } from 'lucide-react';

const PRESETS = {
  Insulin: 'AGCCCTCCAGGACAGGCTGCATCAGAAGAGGCCATCAAGCACATCACTGTCCTTCTGCCATGGCCCTGTGGATGCGCCTCCTGCCCCTGCTGGCGCTGCTGGCCCTCTGGGGACCTGACCCAGCCGCAGCCTTTGTGAACCAACACCTGTGCGGCTCACACCTGGTGGAAGCTCTCTACCTAGTGTGCGGGGAACGAGGCTTCTTCTACACACCCAAGACCCGCCGGGAGGCAGAGGACCTGCAGGGTGAGCCAA',
  GFP: 'ATGGCTAGCAAAGGAGAAGAACTTTTCACTGGAGTTGTCCCAATTCTTGTTGAATTAGATGGTGATGTTAATGGGCACAAATTTTCTGTCAGTGGAGAGGGTGAAGGTGATGCAACATACGGAAAACTTACCCTTAAATTTATTTGCACTACTGGAAAACTACCTGTTCCGTGGCCAACACTTGTCACTACTTTCGGTTATGGTGTTCAATGCTTTTGCAGATACCCAGATCATATGAAACAGCATGACTT',
  BRCA1_Segment: 'ATGGATTTATCTGCTCTTCGCGTTGAAGAAGTACAAAATGTCATTAATGCTATGCAGAAAATCTTAGAGTGTCCCATCTGTCTGGAGTTGATCAAGGAACCTGTCTCCACAAAGTGTGACCACATATTTTGCAAATTTTGCATGCTGAAACTTCTCAACCAGAAGAAAGGGCCTTCACAATGTCCTTTGTGTAAGAATGATATAACCAAAAGGAGCCTACAAGAAAGTACGAGATTTAGTCAACTTGTTGA'
};

export default function SequenceWorkbench() {
  const [seqName, setSeqName] = useState<keyof typeof PRESETS | 'Custom'>('Insulin');
  const [customSeq, setCustomSeq] = useState('');
  const [copied, setCopied] = useState(false);

  const activeSequence = useMemo(() => {
    if (seqName === 'Insulin' || seqName === 'GFP' || seqName === 'BRCA1_Segment') {
      return PRESETS[seqName].toUpperCase().replace(/[^ATGCU]/g, '');
    }
    return customSeq.toUpperCase().replace(/[^ATGCU]/g, '');
  }, [seqName, customSeq]);

  const metrics = useMemo(() => {
    const len = activeSequence.length;
    if (len === 0) return { gcContent: 0, length: 0, translation: '', baseCounts: { A: 0, T: 0, G: 0, C: 0 } };

    let gc = 0;
    const baseCounts = { A: 0, T: 0, G: 0, C: 0 };
    for (let char of activeSequence) {
      if (char === 'G' || char === 'C') gc++;
      if (char in baseCounts) {
        baseCounts[char as keyof typeof baseCounts]++;
      }
    }

    // Translation logic (DNA -> Amino Acid)
    const codonMap: Record<string, string> = {
      ATG: 'M', TGG: 'W', TTY: 'F', TTR: 'L', CTY: 'L', CTR: 'L', ATH: 'I', GTN: 'V',
      TCN: 'S', CCN: 'P', ACN: 'T', GCN: 'A', TAY: 'Y', TAA: '*', TAG: '*', TGA: '*',
      CAY: 'H', CAR: 'Q', AAY: 'N', AAR: 'K', GAY: 'D', GAR: 'E', TGY: 'C', CGN: 'R',
      AGY: 'S', AGR: 'R', GGN: 'G', AAA: 'K', AAG: 'K', AAC: 'N', AAT: 'N',
      TTC: 'F', TTT: 'F', TTA: 'L', TTG: 'L', CTC: 'L', CTT: 'L', CTA: 'L', CTG: 'L',
      ATC: 'I', ATT: 'I', ATA: 'I', GTC: 'V', GTT: 'V', GTA: 'V', GTG: 'V',
      TCC: 'S', TCT: 'S', TCA: 'S', TCG: 'S', CCC: 'P', CCT: 'P', CCA: 'P', CCG: 'P',
      ACC: 'T', ACT: 'T', ACA: 'T', ACG: 'T', GCC: 'A', GCT: 'A', GCA: 'A', GCG: 'A',
      TAC: 'Y', TAT: 'Y', TGC: 'C', TGT: 'C', CGA: 'R', CGC: 'R', CGG: 'R', CGT: 'R',
      AGC: 'S', AGT: 'S', AGA: 'R', AGG: 'R', GGA: 'G', GGC: 'G', GGG: 'G', GGT: 'G',
      CAC: 'H', CAT: 'H', CAA: 'Q', CAG: 'Q', GAC: 'D', GAT: 'D', GAA: 'E', GAG: 'E'
    };

    let translation = '';
    for (let i = 0; i < activeSequence.length - 2; i += 3) {
      const codon = activeSequence.substring(i, i + 3);
      translation += codonMap[codon] || '?';
    }

    return {
      gcContent: (gc / len) * 100,
      length: len,
      translation,
      baseCounts
    };
  }, [activeSequence]);

  const handleCopy = () => {
    navigator.clipboard.writeText(metrics.translation);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className='bg-card border-border my-8 rounded-2xl border p-6 md:p-8 shadow-xl space-y-6'>
      <div className='flex items-center gap-3 border-b border-border/40 pb-4'>
        <div className='flex size-10 items-center justify-center rounded-xl bg-brand-dark/10 dark:bg-brand-dark-text-primary/10'>
          <Dna className='size-5 text-brand-dark dark:text-brand-dark-text-primary' />
        </div>
        <div>
          <h4 className='text-foreground text-xl font-serif font-black tracking-tight'>
            Dry-Lab Sequence Workbench
          </h4>
          <p className='text-muted-foreground text-xs font-medium'>
            Simulate genomic sequence analysis, codon frequency distribution, and peptide translations in real-time.
          </p>
        </div>
      </div>

      {/* Preset / Input Selectors */}
      <div className='flex flex-wrap gap-2.5'>
        {Object.keys(PRESETS).map((preset) => (
          <button
            key={preset}
            onClick={() => setSeqName(preset as any)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
              seqName === preset
                ? 'bg-brand-dark text-white border-brand-dark'
                : 'text-muted-foreground hover:text-foreground border-border bg-muted/10'
            }`}
          >
            {preset.replace('_Segment', '')} Preset
          </button>
        ))}
        <button
          onClick={() => {
            setSeqName('Custom' as any);
            setCustomSeq('');
          }}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
            seqName === 'Custom'
              ? 'bg-brand-dark text-white border-brand-dark'
              : 'text-muted-foreground hover:text-foreground border-border bg-muted/10'
          }`}
        >
          Custom Input
        </button>
      </div>

      {/* Textarea for Custom Input */}
      {seqName === 'Custom' && (
        <textarea
          value={customSeq}
          onChange={(e) => setCustomSeq(e.target.value.replace(/[^ATGCUatgcu\s]/g, ''))}
          placeholder='Paste DNA sequence here (A, T, G, C only)...'
          className='w-full h-24 p-3 bg-muted/30 border border-border rounded-xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-foreground uppercase tracking-wider'
        />
      )}

      {/* Metrics Grid */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {/* GC Content Dial */}
        <div className='bg-muted/10 border border-border/20 p-5 rounded-xl flex flex-col justify-between items-center text-center'>
          <span className='text-[10px] font-black uppercase text-muted-foreground tracking-widest'>GC Content</span>
          <div className='relative flex items-center justify-center my-3'>
            {/* SVG Progress Circle */}
            <svg className='size-24 transform -rotate-90'>
              <circle cx='48' cy='48' r='40' className='stroke-muted/30 fill-none' strokeWidth='8' />
              <circle
                cx='48' cy='48' r='40'
                className='stroke-emerald-600 dark:stroke-emerald-400 fill-none transition-all duration-700'
                strokeWidth='8'
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - metrics.gcContent / 100)}`}
              />
            </svg>
            <span className='absolute text-lg font-mono font-black text-foreground'>{metrics.gcContent.toFixed(1)}%</span>
          </div>
          <span className='text-xs text-muted-foreground font-semibold'>Total Nucleotides: {metrics.length}</span>
        </div>

        {/* Nucleotide Breakdown Chart */}
        <div className='bg-muted/10 border border-border/20 p-5 rounded-xl space-y-4 md:col-span-2'>
          <span className='text-[10px] font-black uppercase text-muted-foreground tracking-widest flex items-center gap-1.5'>
            <BarChart2 className='size-3.5' /> Nucleotide Distribution
          </span>
          <div className='space-y-2.5'>
            {Object.entries(metrics.baseCounts).map(([base, count]) => {
              const pct = metrics.length ? (count / metrics.length) * 100 : 0;
              return (
                <div key={base} className='space-y-1'>
                  <div className='flex justify-between text-xs font-semibold'>
                    <span className='text-foreground font-mono'>{base}</span>
                    <span className='text-muted-foreground font-mono'>{count} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div className='h-2 bg-muted/40 rounded-full overflow-hidden'>
                    <div
                      style={{ width: `${pct}%` }}
                      className='h-full bg-emerald-600 dark:bg-emerald-400 rounded-full transition-all duration-500'
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Translation Output */}
      <div className='bg-muted/10 border border-border/20 p-5 rounded-xl space-y-3'>
        <div className='flex justify-between items-center'>
          <span className='text-[10px] font-black uppercase text-muted-foreground tracking-widest'>Amino Acid Translation</span>
          <button
            onClick={handleCopy}
            className='flex items-center gap-1.5 text-xs text-[#066653] font-black hover:underline cursor-pointer'
          >
            {copied ? (
              <>
                <CheckCircle2 className='size-3.5 text-emerald-600' />
                Copied Translation
              </>
            ) : (
              <>
                <RefreshCw className='size-3.5' />
                Copy Sequence
              </>
            )}
          </button>
        </div>
        <div className='h-24 overflow-y-auto bg-muted/40 border border-border/30 rounded-lg p-3 text-xs font-mono text-foreground break-all leading-relaxed select-all uppercase'>
          {metrics.translation || 'No translation available. Input sequence first.'}
        </div>
      </div>
    </div>
  );
}
